import Stack from "components/common/Stack";
import Text from "components/common/Text";
import React, { useState, useRef, useEffect } from "react";
import { useHistory } from "react-router-dom";
import DownloadIcon from "@mui/icons-material/Download";
import UploadIcon from "@mui/icons-material/Upload";
import CircularProgress from "@mui/material/CircularProgress";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import { PHCOnboardingApis } from "stores/PHC/PHCOnboarding/PHCOnboardingApis";
import { useDispatch, useSelector } from "react-redux";
import { PHCOnboardingActions } from "stores/PHC/PHCOnboarding/PHCOnboardingAction";
import { PHCDashboardApis } from "stores/PHC/PHCDashoard/PHCDashboardApis";
import { PHCDashboardAction } from "stores/PHC/PHCDashoard/PHCDashboardAction";
import { commonActions } from "stores/common/commonActions";
import { PHCOnboardingParser } from "stores/PHC/PHCOnboarding/PHCOnboardingParser";
import { resolvePhcUploadPortfolioId } from "helper/phcPortfolio";
import Modal from "components/common/Modal";
import { Capacitor } from "@capacitor/core";
import { config } from "config";
import { PHCOnboardingConstants } from "stores/PHC/PHCOnboarding/PHCOnboardingConstants";

const EXCEL_MIME =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
const EXCEL_FILE_NAME = "PHC_Template.xlsx";

const CONSENT_POLL_LIMIT = 35;

const readConsentStatus = (res) =>
  res?.status || res?.result?.status || res?.response?.status || "";

const readPortfolioList = (response) => {
  const nested = response?.result ?? response?.response?.result ?? response;
  if (Array.isArray(nested?.list)) return nested.list;
  if (Array.isArray(nested)) return nested;
  return [];
};

const pickNewPortfolioId = (list, existingIds) => {
  const rows = Array.isArray(list) ? list : [];
  const known = existingIds instanceof Set ? existingIds : new Set();

  const fresh = rows.find((p) => p?.portfolioId && !known.has(p.portfolioId));
  if (fresh) return fresh.portfolioId;

  const pending = rows.find((p) => p?.portfolioId && p.updatedAt == null);
  return pending?.portfolioId || null;
};

function UploadPortfolio(props) {
  const [file, setFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);

  const isPhcOnboardingCompleted = useSelector(
    (state) => state.PHC.PHCOnboarding.isOnboardingCompleted,
  );
  const { enableOnemoney } = useSelector((state) => state.common.featureConfig);
  const portfolios = useSelector((state) => state.PHC.PHCDashboard.list);

  const dispatch = useDispatch();
  const history = useHistory();
  const dropRef = useRef(null);
  const pollCountRef = useRef(0);
  const consentApprovedRef = useRef(false);
  const existingIdsRef = useRef(new Set());
  const getPortfolioIdCbRef = useRef(props?.getportfolioId);
  getPortfolioIdCbRef.current = props?.getportfolioId;

  const [isOneMoneyIfram, setIsOneMoneyIfram] = useState("");
  const [isOneMoneyModalOpen, setIsOneMoneyModalOpen] = useState(false);
  const [isSyncingHoldings, setIsSyncingHoldings] = useState(false);

  // ===== Drag & Drop handlers =====
  const handleDragOver = (e) => {
    e.preventDefault();
    dropRef.current.style.borderColor = "#3b82f6";
    dropRef.current.style.background = "#f9fbff";
  };

  const handleDragLeave = () => {
    dropRef.current.style.borderColor = "#c1c7d0";
    dropRef.current.style.background = "#fff";
  };

  const handleDrop = (e) => {
    e.preventDefault();
    handleDragLeave();
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) handleFileSelection(droppedFile);
  };

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) handleFileSelection(selectedFile);
  };

  const handleFileSelection = (selectedFile) => {
    const allowedTypes = [".xlsx", ".xls"];
    const fileExtension = selectedFile.name.split(".").pop().toLowerCase();

    if (!allowedTypes.includes(`.${fileExtension}`)) {
      setUploadError("Please upload only Excel files (.xlsx or .xls)");
      return;
    }

    const maxSize = 10 * 1024 * 1024;
    if (selectedFile.size > maxSize) {
      setUploadError("File size should be less than 10MB");
      return;
    }

    setFile(selectedFile);
    setUploadError(null);
    setUploadSuccess(false);

    // Automatically upload after selection
    uploadExcelFile(selectedFile);
  };

  // ===== Upload Excel =====
  const uploadExcelFile = async (fileToUpload) => {
    if (!fileToUpload) return;

    setIsUploading(true);
    setUploadError(null);
    setUploadSuccess(false);

    try {
      const response = await PHCOnboardingApis.uploadExcel(fileToUpload);

      setUploadSuccess(true);

      const portfolioId = resolvePhcUploadPortfolioId(response);

      if (!portfolioId) {
        dispatch(
          commonActions.setToast({
            message:
              "File uploaded but portfolio could not be loaded. Please try again.",
            type: "warning",
          }),
        );
      } else {
        dispatch(
          PHCOnboardingActions.updatePHCOnboardingData({
            portfolioId,
          }),
        );

        if (isPhcOnboardingCompleted === 1) {
          dispatch(PHCDashboardAction.setCompletedPortfolio(portfolioId));
        }

        dispatch(PHCDashboardAction.setFromOneMoney(false));
        dispatch(PHCDashboardAction.setJustUploadedPortfolio(portfolioId));

        if (typeof props?.getportfolioId === "function") {
          props.getportfolioId(portfolioId);
        }
      }

      dispatch(PHCOnboardingActions.getOnboardingData());

      if (props.onUploadSuccess) {
        props.onUploadSuccess(response);
      }
    } catch (error) {
      setUploadError(
        error.response?.data?.message ||
          error.message ||
          "Failed to upload file. Please try again.",
      );
      if (props.onUploadError) props.onUploadError(error);
    } finally {
      setIsUploading(false);
    }
  };

  // ===== Download Template =====
  const downloadTemplate = async () => {
    try {
      // The template URL is public. The phone browser can save it; the WebView cannot.
      if (Capacitor.isNativePlatform()) {
        const base = String(config.apiUrl || "").replace(/\/$/, "");
        const excelUrl = `${base}/${PHCOnboardingConstants.PHC_DOWNLOAD_EXCEL}`;
        if (window.cordova?.InAppBrowser) {
          window.cordova.InAppBrowser.open(excelUrl, "_system");
        } else {
          window.open(excelUrl, "_blank");
        }
        return;
      }

      const response = await PHCDashboardApis.getDownloadExcelPhc();
      const blob = new Blob([response], { type: EXCEL_MIME });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = EXCEL_FILE_NAME;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download failed", error);
      dispatch(
        commonActions.setToast({
          message: "Could not download the Excel file. Please try again.",
          type: "warning",
        }),
      );
    }
  };

  // ===== Remove file =====
  const removeFile = () => {
    setFile(null);
    setUploadError(null);
    setUploadSuccess(false);
    if (props.onFileRemove) props.onFileRemove();
  };

  // ===== OneMoney Click Handler =====
  const handleOneMoneyClick = async () => {
    try {
      setIsUploading(true);

      const res = await PHCOnboardingApis.getOneMoneyRedirectUrl();
      const redirectUrl = res?.redirectionUrl;

      if (!redirectUrl) {
        dispatch(
          commonActions.setToast({
            message: "OneMoney redirection URL not found",
          }),
        );
        return;
      }

      existingIdsRef.current = new Set(
        (portfolios || []).map((p) => p.portfolioId).filter(Boolean),
      );
      try {
        const currentList = readPortfolioList(await PHCDashboardApis.getList());
        existingIdsRef.current = new Set(
          currentList.map((p) => p.portfolioId).filter(Boolean),
        );
      } catch (err) {
        console.error("OneMoney list snapshot failed", err);
      }

      consentApprovedRef.current = false;
      setIsSyncingHoldings(false);
      setIsOneMoneyIfram(redirectUrl);
      setIsOneMoneyModalOpen(true);
    } catch (error) {
      console.error("OneMoney redirect failed", error);
      setUploadError(
        error?.message ||
          "Unable to connect with OneMoney. Please upload Excel instead.",
      );
    } finally {
      setIsUploading(false);
    }
  };
  const detailResponse = async () => {
    const detailRes = await PHCOnboardingApis.getOnboardingData();
    const parsedOnboardingData = PHCOnboardingParser.onboardingData(detailRes);

    return parsedOnboardingData;
  };

  const finishAfterConsent = (latest, portfolioId) => {
    if (!portfolioId) return;

    setIsSyncingHoldings(false);
    setIsOneMoneyModalOpen(false);

    dispatch(PHCDashboardAction.setFromOneMoney(true));
    dispatch(
      PHCOnboardingActions.updatePHCOnboardingData({
        ...(latest || {}),
        portfolioId,
      }),
    );

    const onCreated = getPortfolioIdCbRef.current;
    if (typeof onCreated === "function") {
      onCreated(portfolioId);
    } else {
      history.push("/phc/onboarding/preview");
    }

    dispatch(
      commonActions.setToast({
        message: "Portfolio successfully synced via OneMoney",
        type: "success",
      }),
    );
  };

  const resolvePortfolioIdFromList = async () => {
    const list = readPortfolioList(await PHCDashboardApis.getList());
    return pickNewPortfolioId(list, existingIdsRef.current);
  };

  // Poll consent-status only while the iframe is open and not yet approved.
  useEffect(() => {
    if (!isOneMoneyModalOpen || consentApprovedRef.current) {
      return undefined;
    }

    let cancelled = false;
    let interval;
    pollCountRef.current = 0;

    const tick = async () => {
      if (cancelled) return;

      try {
        pollCountRef.current += 1;

        if (pollCountRef.current >= CONSENT_POLL_LIMIT) {
          clearInterval(interval);
          setIsOneMoneyModalOpen(false);
          dispatch(
            commonActions.setToast({
              message: "Consent not approved. Please try again later.",
              type: "warning",
            }),
          );
          return;
        }

        const detailRes = await detailResponse();
        if (cancelled) return;

        const consentId = detailRes?.consentId;
        if (!consentId) return;

        const consentStatus = await PHCOnboardingApis.getConsentId({
          consentId,
        });
        if (cancelled) return;

        if (readConsentStatus(consentStatus) !== "CONSENT_APPROVED") {
          return;
        }

        consentApprovedRef.current = true;
        clearInterval(interval);

        let portfolioId = null;
        try {
          portfolioId = await resolvePortfolioIdFromList();
        } catch (err) {
          console.error("OneMoney list lookup failed", err);
        }
        if (cancelled) return;

        setIsOneMoneyModalOpen(false);

        if (portfolioId) {
          finishAfterConsent(detailRes, portfolioId);
          return;
        }

        setIsSyncingHoldings(true);
      } catch (err) {
        console.error("Polling OneMoney backend failed", err);
      }
    };

    tick();
    interval = setInterval(tick, 5000);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [isOneMoneyModalOpen]);

  // After approval, poll portfolio/list only — details never has this id.
  useEffect(() => {
    if (!isSyncingHoldings) {
      return undefined;
    }

    let cancelled = false;
    let interval;
    let ticks = 0;

    const tick = async () => {
      if (cancelled) return;

      try {
        ticks += 1;

        const portfolioId = await resolvePortfolioIdFromList();
        if (cancelled) return;

        if (portfolioId) {
          clearInterval(interval);
          finishAfterConsent(null, portfolioId);
          return;
        }

        if (ticks >= CONSENT_POLL_LIMIT) {
          clearInterval(interval);
          setIsSyncingHoldings(false);
          dispatch(PHCDashboardAction.fetchPortfolioRequest());
          dispatch(
            commonActions.setToast({
              message:
                "Holdings synced, but the new portfolio could not be opened. Please select it from the list.",
              type: "warning",
            }),
          );
        }
      } catch (err) {
        console.error("OneMoney holdings sync failed", err);
      }
    };

    tick();
    interval = setInterval(tick, 5000);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [isSyncingHoldings]);

  return (
    <Stack gap={2}>
      <Stack>
        <Text variant="subtitle2" color="#202020">
          Upload Portfolio
        </Text>
      </Stack>

      <Stack
        direction="row"
        alignItems="flex-start"
        spacing={1.25}
        sx={{
          px: 1.75,
          py: 1.5,
          borderRadius: "8px",
          backgroundColor: "#EEF4FA",
          border: "1px solid #D6E4F0",
        }}
      >
        <InfoOutlined sx={{ fontSize: 20, color: "#2B70C5", mt: "2px" }} />
        <Stack spacing={0.75}>
          <Text
            sx={{
              
              fontSize: 13,
              fontWeight: 600,
              color: "#252C32",
              lineHeight: 1.4,
            }}
          >
            Please note <Text sx={{ display: "inline", fontSize: 13, color: "#303236", lineHeight: 1.5 }}>
            The PHC report will present analysis solely for NSE stocks where
            requisite data is accessible.
          </Text>
          </Text>
          
          <Text
            sx={{
              fontSize: 13,
              fontWeight: 600,
              color: "#DC2626",
              lineHeight: 1.5,
            }}
          >
            If data is uploaded without a Buy Date, the report will
            automatically be generated on a 1-year time frame.
          </Text>
        </Stack>
      </Stack>

      <div
        style={{
          display: "flex",
          justifyContent: enableOnemoney ? "space-around" : "center",
          alignItems: "center",
          gap: "16px",
          marginBottom: "16px",
        }}
      >
        {enableOnemoney && (
          <button
            onClick={handleOneMoneyClick}
            style={oneMoneyButtonStyle}
            disabled={isUploading || isSyncingHoldings}
          >
            Get Holdings
          </button>
        )}

        <button
          onClick={downloadTemplate}
          style={oneMoneyButtonStyle}
          disabled={isUploading}
        >
          <DownloadIcon fontSize="small" />
          Download Template
          <span
            style={{
              marginLeft: "4px",
              fontSize: "12px",
              opacity: 0.8,
              fontWeight: "normal",
            }}
          >
            (.xlsx)
          </span>
        </button>
      </div>
      {uploadError && <div style={errorStyle}>{uploadError}</div>}
      {uploadSuccess && (
        <div style={successStyle}>
          File uploaded successfully! Processing your portfolio...
        </div>
      )}

      <div
        ref={dropRef}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() =>
          !file &&
          !isUploading &&
          document.getElementById("hidden-file-input").click()
        }
        style={dropAreaStyle(isUploading)}
      >
        {isUploading && (
          <div style={uploadOverlayStyle}>
            <Stack align="center" gap={2}>
              <CircularProgress size={40} style={{ color: "#3b82f6" }} />
              <Text variant="body2" color="#6b7280">
                Uploading file...
              </Text>
            </Stack>
          </div>
        )}

        <Stack align="center" gap={4}>
          {!file ? (
            <>
              <div>
                <UploadIcon
                  style={{
                    fontSize: 48,
                    color: isUploading ? "#cbd5e1" : "#94a3b8",
                    marginBottom: "16px",
                  }}
                />
              </div>
              <Stack align="center" gap={1}>
                <Text
                  variant="body2"
                  color={isUploading ? "#cbd5e1" : "#6b7280"}
                >
                  Drag & drop to upload
                </Text>
                <Text
                  variant="body2"
                  color="#3b82f6"
                  style={{ fontWeight: 600 }}
                >
                  or Browse
                </Text>
                <Text
                  variant="caption"
                  color="#94a3b8"
                  style={{ marginTop: "8px" }}
                >
                  Supports .xlsx, .xls (Max 10MB)
                </Text>
              </Stack>
            </>
          ) : (
            <Stack align="center" gap={3}>
              <div
                style={{
                  padding: "12px",
                  backgroundColor: uploadSuccess ? "#d1fae5" : "#f0f9ff",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: "1px solid red",
                }}
              >
                {uploadSuccess ? (
                  <span style={{ fontSize: "32px", color: "#10b981" }}>✓</span>
                ) : (
                  <UploadIcon style={{ fontSize: 32, color: "#3b82f6" }} />
                )}
              </div>

              <Stack align="center">
                <Text variant="body2" color="#202020">
                  <strong>{file.name}</strong>
                </Text>
                <Text variant="caption" color="#64748b">
                  {(file.size / 1024 / 1024).toFixed(2)} MB
                </Text>
              </Stack>

              {!isUploading && !uploadSuccess && (
                <div
                  style={{
                    display: "flex",
                    gap: "12px",
                    marginTop: "8px",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <button
                    onClick={() => uploadExcelFile(file)}
                    disabled={isUploading}
                    style={uploadNowBtnStyle}
                  >
                    <UploadIcon fontSize="small" /> Upload Now
                  </button>
                  <button onClick={removeFile} style={removeBtnStyle}>
                    Remove
                  </button>
                </div>
              )}
            </Stack>
          )}
        </Stack>
      </div>

      <input
        id="hidden-file-input"
        type="file"
        accept=".xlsx,.xls"
        style={{ display: "none" }}
        onChange={handleFileSelect}
        disabled={isUploading}
      />

      <Modal
        open={isOneMoneyModalOpen}
        onClose={() => {
          if (consentApprovedRef.current) return;
          setIsOneMoneyModalOpen(false);
        }}
        size="xl"
        style={{ height: "100vh" }}
      >
        <iframe
          src={isOneMoneyIfram}
          style={{
            width: "100%",
            height: "100vh",
            border: "none",
          }}
          allow="camera; microphone; payment"
        />
      </Modal>

      {isSyncingHoldings ? (
        <div style={syncOverlayStyle}>
          <div style={syncCardStyle}>
            <CircularProgress size={36} style={{ color: "#2977EC" }} />
            <Text
              sx={{
                display: "block",
                fontSize: 18,
                fontWeight: 700,
                color: "#111827",
                mt: 2,
                mb: 1,
              }}
            >
              Syncing holdings
            </Text>
            <Text sx={{ fontSize: 14, color: "#6B7280", lineHeight: 1.5 }}>
              Consent is complete. Please wait while we load your portfolio.
            </Text>
          </div>
        </div>
      ) : null}
    </Stack>
  );
}

// ===== Styles =====
const oneMoneyButtonStyle = {
  display: "flex",
  alignItems: "center",
  gap: "10px",
  padding: "12px 24px",
  backgroundColor: "white",
  color: "#3b82f6",
  border: "2px solid #3b82f6",
  borderRadius: "10px",
  cursor: "pointer",
  fontSize: "15px",
  fontWeight: "600",
  transition: "all 0.3s ease",
  boxShadow: "0 2px 8px rgba(59, 130, 246, 0.1)",
  marginBottom: "10px",
};

const dropAreaStyle = (isUploading) => ({
  border: "2px dashed #c1c7d0",
  borderRadius: "16px",
  padding: "80px 24px",
  textAlign: "center",
  background: "#f8f9fa",
  cursor: isUploading ? "not-allowed" : "pointer",
  transition: "0.2s ease",
  opacity: isUploading ? 0.7 : 1,
  position: "relative",
});

const uploadOverlayStyle = {
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "rgba(255, 255, 255, 0.8)",
  borderRadius: "16px",
  zIndex: 10,
};

const syncOverlayStyle = {
  position: "fixed",
  inset: 0,
  zIndex: 1400,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "rgba(15, 23, 42, 0.45)",
  padding: 16,
};

const syncCardStyle = {
  width: "100%",
  maxWidth: 420,
  backgroundColor: "#FFFFFF",
  borderRadius: 16,
  border: "1px solid #E5E7EB",
  boxShadow: "0 20px 50px rgba(15,23,42,0.22)",
  padding: 32,
  textAlign: "center",
};

const errorStyle = {
  padding: "12px 16px",
  backgroundColor: "#fee",
  border: "1px solid #fcc",
  borderRadius: "8px",
  color: "#d00",
  fontSize: "14px",
  marginBottom: "16px",
};

const successStyle = {
  padding: "12px 16px",
  backgroundColor: "#e8f7ef",
  border: "1px solid #c3e6cb",
  borderRadius: "8px",
  color: "#155724",
  fontSize: "14px",
  marginBottom: "16px",
};

const uploadNowBtnStyle = {
  padding: "8px 16px",
  backgroundColor: "#3b82f6",
  color: "white",
  border: "none",
  borderRadius: "6px",
  cursor: "pointer",
  fontSize: "14px",
  fontWeight: "500",
  display: "flex",
  alignItems: "center",
  gap: "6px",
};

const removeBtnStyle = {
  padding: "8px 16px",
  backgroundColor: "transparent",
  color: "#64748b",
  border: "1px solid #e2e8f0",
  borderRadius: "6px",
  cursor: "pointer",
  fontSize: "14px",
};

export default UploadPortfolio;
